import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { workAuthorService } from "../services/workAuthorService";
import {
  WorkAuthorValuesInit,
  getWorkAuthorValues,
  WorkAuthorValuesType,
  WorkAuthorErrorsType,
  workAuthorDisplayId,
} from "./WorkAuthorCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { WorkAuthor, SortOrder } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getAuthorComposerValues,
  AuthorComposerValuesType,
  authorComposerDisplayId,
} from "../../authorComposer/store/AuthorComposerCommonStore";
import { isNil } from "../../../common/utils";
import { WorkAuthorPublisherUpdateStore } from "../../workAuthorPublisher/store";
import { GridChangeSet } from "../../common/types";
import { RowCrudStatus } from "../../common/types";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { authorComposerService } from "../../authorComposer/services/authorComposerService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class WorkAuthorUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: WorkAuthorErrorsType = {};
  service = workAuthorService;
  workAuthorInstance: WorkAuthor | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = WorkAuthorValuesInit();
  authorError: string | undefined;
  authorService = authorComposerService;
  authorSearchValue: string = "";
  authorOptions: AuthorComposerValuesType[] = [];
  lyricsPercentageError: string | undefined;
  musicPercentageError: string | undefined;
  publishersError: string | undefined;
  publishersVisibleColumns: { name: string; title: string }[];
  publishersColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  publishersUpdateStore: WorkAuthorPublisherUpdateStore;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<WorkAuthorValuesType>[] = [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.publishersVisibleColumns = [
      {
        name: "__publisher",
        title: this.langStore.WorkAuthorPublisherPublisherLabel,
      },
      {
        name: "percentage",
        title: this.langStore.WorkAuthorPublisherPercentageLabel,
      },
      {
        name: "durationInYears",
        title: this.langStore.WorkAuthorPublisherDurationInYearsLabel,
      },
      {
        name: "autoRenewalYears",
        title: this.langStore.WorkAuthorPublisherAutoRenewalYearsLabel,
      },
    ];
    this.publishersColumnTypes = {
      __publisher: {
        fieldType: "__Reference" as const,
        nullable: false,
        readOnly: false,
        multi: false,
        onChange: (value: string) =>
          this.publishersUpdateStore.setPublisherValue(value),
        onSelect: (value: any) =>
          this.publishersUpdateStore.setSelectedPublisher(value),
        getOptions: () => this.publishersUpdateStore.getPublisherOptions(),
      },
      percentage: {
        fieldType: "Decimal" as const,
        nullable: false,
        readOnly: false,
      },
      durationInYears: {
        fieldType: "Int" as const,
        nullable: true,
        readOnly: false,
        choices: {
          min: 1,
          max: 100,
          unitSingular: "año",
          unitPlural: "años",
          nullText: this.langStore.WorkAuthorPublisherDurationInYearsNullText,
        },
      },
      autoRenewalYears: {
        fieldType: "Int" as const,
        nullable: true,
        readOnly: false,
        choices: {
          min: 1,
          max: 50,
          unitSingular: "año",
          unitPlural: "años",
          nullText: this.langStore.WorkAuthorPublisherAutoRenewalYearsNullText,
        },
      },
    };
    this.publishersUpdateStore = new WorkAuthorPublisherUpdateStore(
      rootStore,
      "publishers_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<WorkAuthorValuesType>
        );
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }

    if (this.publishersUpdateStore) {
      this.publishersUpdateStore.destroy();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<WorkAuthorValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadWorkAuthorById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.workAuthorInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.workAuthorInstance) {
      this.entityValues = getWorkAuthorValues(
        this.workAuthorInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda.
  get saveDisabled() {
    return this.isProcessing;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return workAuthorDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.workAuthorInstance) {
      return workAuthorDisplayId(this.workAuthorInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.WorkAuthorSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  viewFormAuthor() {
    const id = this.entityValues.showAuthor?.id;
    if (id) {
      this.rootStore.openOrPushOverlay("authorComposer", id, "view");
    }
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupWorkAuthorLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity
      if (callerData?.entityName === "WorkAuthorPublisher") {
        this.publishersUpdateStore.isWaitingForCallback = false;
        this.publishersUpdateStore.callbackReturnPath = null;
        yield this.publishersUpdateStore.returnedData(data);
        return;
      }

      if (callerData?.fieldName === "author") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            authorComposerDisplayId(returnData.entity, this.langStore);
          this.entityValues.showAuthor = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.authorComposerOne({
            id: returnData.resultId,
          });
          const authorComposer = result.authorComposerOne;
          this.entityValues.showAuthor = {
            displayId: authorComposer.displayId,
            id: authorComposer.id,
          };
          this.setAuthorOptions([
            getAuthorComposerValues(authorComposer, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  setAuthorOptions(values: AuthorComposerValuesType[]) {
    this.authorOptions = values;
  }

  *loadAuthorValues(): any {
    let where: any = null;
    if (this.authorSearchValue) {
      const searchFilter = {
        OR: [
          {
            firstName: {
              contains: this.authorSearchValue,
              mode: "insensitive",
            },
          },
          {
            lastName: { contains: this.authorSearchValue, mode: "insensitive" },
          },
          { aka: { contains: this.authorSearchValue, mode: "insensitive" } },
        ],
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.authorComposerListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ lastUpdatedAt_: SortOrder.Desc }],
    });
    this.setAuthorOptions(
      (result.authorComposerListWithOffset || []).map((x: any) =>
        getAuthorComposerValues(x, this.langStore)
      )
    );
  }

  getAuthorOptions() {
    return this.authorOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setAuthorValue(value: string) {
    this.authorSearchValue = value;
    yield this.loadAuthorValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedAuthor(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewAuthorComposer(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "author"
      );
    } else {
      if (value) {
        this.entityValues.showAuthor = value;
        this.entityValues.__author = value.displayId;

        // Copiar AuthorPublisher del autor como WorkAuthorPublisher iniciales
        yield this.copyAuthorPublishersToWorkAuthor(value.id);
      }
    }
  }

  *copyAuthorPublishersToWorkAuthor(authorId: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.authorComposerOne({ id: authorId });
    const author = result.authorComposerOne;

    if (author?.publishers?.length > 0) {
      this.entityValues.showPublishers = author.publishers.map(
        (ap: any, idx: number) => ({
          id: `new-${idx}`,
          __status__: "added" as const,
          showPublisher: {
            id: ap.publisher.id,
            displayId: ap.publisher.pseudonym || ap.publisher.name,
            name: ap.publisher.name,
          },
          __publisher: ap.publisher.pseudonym || ap.publisher.name,
          percentage: ap.percentage,
          durationInYears: ap.durationInYears,
        })
      );
    }
  }

  getLyricsPercentageValue(): number {
    return this.entityValues.lyricsPercentage;
  }

  setLyricsPercentageValue(value: number) {
    this.entityValues.lyricsPercentage = value;
  }

  getMusicPercentageValue(): number {
    return this.entityValues.musicPercentage;
  }

  setMusicPercentageValue(value: number) {
    this.entityValues.musicPercentage = value;
  }

  private get publishershasChanges() {
    return (
      this.entityValues.showPublishers?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  publishersLoadRow(row: any) {
    this.publishersUpdateStore.loadValuesFromRow(row);
  }

  publishersRestore(id: string) {
    this.entityValues.showPublishers = this.entityValues.showPublishers.map(
      (item) =>
        item.id === id
          ? { ...item, __status__: restoredStatus(item.__status__) }
          : item
    );
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  publishersValidateChanges(_changes: GridChangeSet) {
    const errors = this.publishersUpdateStore.validateData(
      this.publishersUpdateStore.entityValues
    );

    // Uniqueness validation for subentity
    const currentValues = this.publishersUpdateStore.entityValues;
    const existingRows = this.entityValues.showPublishers || [];
    const activeRows = existingRows.filter(
      (row: any) => !row.__status__?.includes("deleted")
    );

    // Uniqueness check: publisher
    const isDuplicate_publisher = activeRows.some(
      (row) =>
        row.id !== currentValues.id &&
        row.showPublisher?.id === currentValues.showPublisher?.id &&
        currentValues.showPublisher?.id
    );
    if (isDuplicate_publisher) {
      errors.publisher = {
        message: this.langStore.duplicateValueError,
        fieldLabel: this.langStore.WorkAuthorPublisherPublisherLabel,
      };
    }

    this.publishersUpdateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  publishersCommitInlineChange(rowId: string, changes: Record<string, any>) {
    this.entityValues.showPublishers = this.entityValues.showPublishers.map(
      (item) =>
        item.id === rowId
          ? { ...item, ...changes, __status__: updatedStatus(item.__status__) }
          : item
    );
  }

  publishersCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.publishersUpdateStore.entityValues;
      const hadChanges = this.publishersUpdateStore.pendingChanges;
      if (hadChanges) {
        const publishers = this.entityValues.showPublishers;
        for (
          let index = 0;
          index < this.entityValues.showPublishers.length;
          index++
        ) {
          if (publishers[index].id === values.id) {
            this.entityValues.showPublishers =
              this.entityValues.showPublishers.map((item, idx) =>
                idx === index
                  ? { ...values, __status__: updatedStatus(values.__status__) }
                  : item
              );
            break;
          }
        }
        this.publishersUpdateStore.clearFields();
      }
    } else if (changes.added) {
      const values = this.publishersUpdateStore.entityValues;
      const newId = `${this.uniqueCounter++}`;
      if (!this.entityValues.showPublishers) {
        this.entityValues.showPublishers = [];
      }
      this.entityValues.showPublishers = [
        ...this.entityValues.showPublishers,
        {
          ...values,
          id: newId,
          __status__: "added",
        },
      ];
      this.publishersUpdateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const publishers = this.entityValues.showPublishers;
      for (
        let index = 0;
        index < this.entityValues.showPublishers.length;
        index++
      ) {
        if (publishers[index].id === id) {
          if (isOriginalCrudStatus(publishers[index].__status__)) {
            this.entityValues.showPublishers =
              this.entityValues.showPublishers.map((item, idx) =>
                idx === index
                  ? { ...item, __status__: deleteStatus(item.__status__) }
                  : item
              );
          } else {
            this.entityValues.showPublishers =
              this.entityValues.showPublishers.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.publishers);
    //   this.setCRUDRowsFromValues(newChanges, this.publishersUpdateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.publishers = getNewCRUDRows(newChanges, this.entityValues.publishers, this.uniqueCounter++);
    // }
  }

  getRowFromInstance(instance: WorkAuthor) {
    const row: any = {};
    row.version_ = instance.version_;
    row.id = instance.id;
    row.lyricsPercentage = instance.lyricsPercentage;
    row.musicPercentage = instance.musicPercentage;
    return row;
  }

  addNewAuthorComposer(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "WorkAuthor",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/authorComposer/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.workAuthorInstance = null;
    this.entityValues = WorkAuthorValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
    this.authorSearchValue = "";
    this.authorOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: WorkAuthorValuesType): WorkAuthorErrorsType {
    const errors: WorkAuthorErrorsType = {};
    this.clearErrors();
    this.authorError = undefined;
    if (values.showAuthor === null || values.showAuthor.id === "") {
      errors.author = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.WorkAuthorAuthorLabel,
      };
    }

    if (isNil(values.lyricsPercentage)) {
      errors.lyricsPercentage = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.WorkAuthorLyricsPercentageLabel,
      };
    }

    if (isNil(values.musicPercentage)) {
      errors.musicPercentage = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.WorkAuthorMusicPercentageLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "WorkAuthorUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges || this.hasArrayChanges();
  }

  private hasArrayChanges(): boolean {
    return this.publishershasChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      lyricsPercentage: this.entityValues.lyricsPercentage,
      musicPercentage: this.entityValues.musicPercentage,
      author: this.entityValues.showAuthor?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  private normalizeSubentitiesAfterSave() {
    this.entityValues.showPublishers = this.entityValues.showPublishers
      .filter(
        (item: any) =>
          item.__status__ !== "original-deleted" &&
          item.__status__ !== "added-deleted"
      )
      .map((item: any) => ({
        ...item,
        __status__: "original" as RowCrudStatus,
      }));
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }
    if (this.publishershasChanges) changed.push("publishers");
    return changed;
  }

  *saveWorkAuthor(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            this.normalizeSubentitiesAfterSave();
            this.resetPendingChanges();
            this.saveOriginalState();
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelWorkAuthor(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getWorkAuthorValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = WorkAuthorValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    // Subentidad sin edición standalone: se persiste por el update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadWorkAuthorById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadAuthorValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
